import { UNSAFE_createBrowserHistory as createBrowserHistory } from "react-router-dom";
import { legacyTeamsPath } from "./teamsRoutes";

// Public URL scheme: every PWHL page lives under /pwhl/… (like /nhl/…). The app's
// routes, page keys and saved per-page settings keep their internal paths — this
// layer translates internal ⇄ public, so the address bar, links and shares are clean.
const PAIRS = [
    ["/overview", "/pwhl"], ["/standings", "/pwhl/standings"], ["/schedule", "/pwhl/schedule"],
    ["/team-leaderboards", "/pwhl/leaderboards"], ["/rosters", "/pwhl/rosters"], ["/active-lineup", "/pwhl/lineups"],
    ["/awards", "/pwhl/awards"], ["/contracts", "/pwhl/contracts"], ["/team-movements", "/pwhl/history"],
    ["/team-trades", "/pwhl/trades"], ["/team-draft", "/pwhl/draft"], ["/staff", "/pwhl/staff"],
    ["/staff-moves", "/pwhl/staff-moves"], ["/injuries", "/pwhl/injuries"], ["/chemistry", "/pwhl/chemistry"],
    ["/team-demographics", "/pwhl/demographics"], ["/milestones", "/pwhl/milestones"], ["/records", "/pwhl/records"],
    ["/rankings", "/pwhl/rankings"], ["/team-news", "/pwhl/news"], ["/team-videos", "/pwhl/videos"],
    ["/team-pages", "/pwhl/teams"], ["/teams", "/pwhl/team"], ["/players", "/pwhl/player"], ["/coaches", "/pwhl/coach"],
    ["/game", "/pwhl/game"], ["/predictions", "/pwhl/predictions"], ["/playstyles", "/pwhl/playstyles"],
];
// Old duplicate pages — open the same public page.
const ONE_WAY = [
    ["/leaderboards", "/pwhl/leaderboards"], ["/trades", "/pwhl/trades"], ["/draft", "/pwhl/draft"],
    ["/transfers", "/pwhl/history"], ["/demographics", "/pwhl/demographics"], ["/team-records", "/pwhl/records"],
    ["/team-history", "/pwhl/history"], ["/news", "/pwhl/news"], ["/videos", "/pwhl/videos"],
];
const EXACT_ONLY = new Set(["/overview", "/pwhl"]);
const byLen = (i) => (a, b) => b[i].length - a[i].length;
const TO_PRETTY = [...PAIRS, ...ONE_WAY].sort(byLen(0));
const TO_LEGACY = [...PAIRS].sort(byLen(1));

const swap = (path, table, from, to) => {
    for (const row of table) {
        const p = row[from];
        if (path === p) return row[to];
        if (!EXACT_ONLY.has(p) && path.startsWith(p + "/")) return row[to] + path.slice(p.length);
    }
    return path;
};
export const toPretty = (path = "") => swap(path, TO_PRETTY, 0, 1);
export const toLegacy = (path = "") => swap(path, TO_LEGACY, 1, 0);
// Internal path of the current page (use instead of window.location.pathname).
export const legacyPath = () => toLegacy(window.location.pathname);

// Old /teams?tab=X links → that tab's clean page.
const fixTeams = (to) => {
    if (typeof to !== "string" || !to.startsWith("/teams?")) return to;
    const sp = new URLSearchParams(to.slice(7));
    const dest = legacyTeamsPath(sp);
    sp.delete("tab"); sp.delete("team");
    const rest = sp.toString();
    return dest + (rest ? `?${rest}` : "");
};
const prettyTo = (to0) => {
    const to = fixTeams(to0);
    if (typeof to === "string") {
        const i = to.search(/[?#]/);
        return i < 0 ? toPretty(to) : toPretty(to.slice(0, i)) + to.slice(i);
    }
    return to && to.pathname ? { ...to, pathname: toPretty(to.pathname) } : to;
};
const legacyLoc = (loc) => ({ ...loc, pathname: toLegacy(loc.pathname) });

export function createPrettyHistory() {
    const { pathname, search, hash } = window.location;
    const pretty = toPretty(pathname);
    if (pretty !== pathname) window.history.replaceState(window.history.state, "", pretty + search + hash);
    const base = createBrowserHistory({ v5Compat: true });
    return {
        get action() { return base.action; },
        get location() { return legacyLoc(base.location); },
        createHref: (to) => base.createHref(prettyTo(to)),
        createURL: (to) => base.createURL(prettyTo(to)),
        encodeLocation: (to) => legacyLoc(base.encodeLocation(prettyTo(to))),
        push: (to, state) => base.push(prettyTo(to), state),
        replace: (to, state) => base.replace(prettyTo(to), state),
        go: (n) => base.go(n),
        listen: (fn) => base.listen(({ action, location, delta }) => fn({ action, location: legacyLoc(location), delta })),
    };
}
